import { useCallback, useEffect, useRef, useState } from 'react';
import WaveformGenerator from '@/services/media/WaveformGenerator';

interface WaveformSegment {
    startTime: number;
    endTime: number;
    peaks: number[];
}

interface UseWaveformOptions {
    peaksPerSecond?: number;
    maxRetryAttempts?: number;
}

interface UseWaveformReturn {
    peaksCache: Map<string, WaveformSegment>;
    isLoading: boolean;
    generateWaveformSegment: (startTime: number, endTime: number) => Promise<void>;
    loadAudioFile: (file: File) => Promise<void>;
    clearCache: () => void;
}

export function useWaveform({ peaksPerSecond = 50, maxRetryAttempts = 3 }: UseWaveformOptions = {}): UseWaveformReturn {
    const waveformGeneratorRef = useRef<WaveformGenerator | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [peaksCache, setPeaksCache] = useState<Map<string, WaveformSegment>>(new Map());
    const [errorCache, setErrorCache] = useState<Map<string, number>>(new Map());
    const generationQueueRef = useRef<Promise<Set<string>>>(Promise.resolve(new Set()));

    useEffect(() => {
        if (!waveformGeneratorRef.current) {
            waveformGeneratorRef.current = new WaveformGenerator({
                peaksPerSecond
            });
        }

        return () => {
            if (waveformGeneratorRef.current) {
                waveformGeneratorRef.current.destroy().catch(console.warn);
                waveformGeneratorRef.current = null;
            }
        };
    }, [peaksPerSecond]);

    const loadAudioFile = useCallback(async (file: File) => {
        if (!waveformGeneratorRef.current) return;

        try {
            const currentFile = waveformGeneratorRef.current.getCurrentFile();
            if (currentFile && currentFile.name === file.name && currentFile.size === file.size) {
                return;
            }

            clearCache();

            await waveformGeneratorRef.current.loadFile(file);
            setIsLoading(false);
        } catch (error) {
            console.warn('Failed to load audio file:', error);
        }
    }, []);

    const getSegmentKey = useCallback((startTime: number, endTime: number): string => {
        return `${Math.round(startTime * 100)}-${Math.round(endTime * 100)}`;
    }, []);

    const isRangeCovered = useCallback((startTime: number, endTime: number): boolean => {
        const segments = Array.from(peaksCache.values());
        segments.sort((a, b) => a.startTime - b.startTime);

        let currentTime = startTime;
        for (const segment of segments) {
            if (segment.endTime <= currentTime) continue;
            if (segment.startTime >= endTime) break;
            if (segment.startTime > currentTime) return false;
            currentTime = Math.max(currentTime, segment.endTime);
            if (currentTime >= endTime) return true;
        }
        return currentTime >= endTime;
    }, [peaksCache]);

    const findCoverageGaps = useCallback((startTime: number, endTime: number): Array<{
        startTime: number,
        endTime: number
    }> => {
        const segments = Array.from(peaksCache.values());
        segments.sort((a, b) => a.startTime - b.startTime);

        const gaps: Array<{ startTime: number, endTime: number }> = [];
        let currentTime = startTime;

        for (const segment of segments) {
            if (segment.endTime <= currentTime) continue;
            if (segment.startTime >= endTime) {
                gaps.push({startTime: currentTime, endTime});
                break;
            }
            if (segment.startTime > currentTime) {
                gaps.push({startTime: currentTime, endTime: Math.min(segment.startTime, endTime)});
            }
            currentTime = Math.max(currentTime, segment.endTime);
            if (currentTime >= endTime) break;
        }

        if (currentTime < endTime) {
            gaps.push({startTime: currentTime, endTime});
        }
        return gaps;
    }, [peaksCache]);

    const areAllGapsFailed = useCallback((gaps: Array<{startTime: number, endTime: number}>): boolean => {
        return gaps.every(gap => {
            const segmentKey = getSegmentKey(gap.startTime, gap.endTime);
            const failedAttempts = errorCache.get(segmentKey) || 0;
            return failedAttempts >= maxRetryAttempts;
        });
    }, [errorCache, getSegmentKey, maxRetryAttempts]);

    const generateWaveformSegment = useCallback(async (startTime: number, endTime: number) => {
        if (!waveformGeneratorRef.current || isLoading) {
            return
        }

        if (isRangeCovered(startTime, endTime)) {
            return;
        }

        const gaps = findCoverageGaps(startTime, endTime);
        if (gaps.length === 0) {
            return;
        }

        if (areAllGapsFailed(gaps)) {
            return;
        }

        // Serialize waveform generation to prevent parallel processing race conditions
        generationQueueRef.current = generationQueueRef.current.then(async (prevSegments) => {
            const currentSegments = new Set<string>();
            let segmentKey = "";
            setIsLoading(true);

            try {
                for (const gap of gaps) {
                    const gapDuration = gap.endTime - gap.startTime;
                    segmentKey = getSegmentKey(gap.startTime, gap.endTime);
                    if(prevSegments.has(segmentKey)) {
                        continue;
                    }

                    const failedAttempts = errorCache.get(segmentKey) || 0;
                    if (failedAttempts >= maxRetryAttempts) {
                        continue;
                    }
                    const segment = await waveformGeneratorRef.current!.generateSegment(
                        gap.startTime,
                        gapDuration
                    );

                    const actualDuration = segment.peaks.length / peaksPerSecond;
                    const correctedEndTime = gap.startTime + actualDuration;

                    setPeaksCache(prev => new Map(prev).set(segmentKey, {
                        startTime: gap.startTime,
                        endTime: correctedEndTime,
                        peaks: segment.peaks
                    }));
                    currentSegments.add(segmentKey);
                }
            } catch (error) {
                console.warn('Waveform generation error:', error);
                setErrorCache(prev => new Map(prev).set(segmentKey, (prev.get(segmentKey) || 0) + 1));
            } finally {
                setIsLoading(false);
            }
            return currentSegments;
        });
    }, [isLoading, getSegmentKey, isRangeCovered, findCoverageGaps, peaksPerSecond, areAllGapsFailed, errorCache, maxRetryAttempts]);

    const clearCache = useCallback(() => {
        setPeaksCache(new Map());
        setErrorCache(new Map());
    }, []);

    useEffect(() => {
        return () => {
            if (waveformGeneratorRef.current && waveformGeneratorRef.current.isFileLoaded()) {
                waveformGeneratorRef.current.unloadFile().catch(console.warn);
            }
        };
    }, []);

    return {
        peaksCache,
        isLoading,
        generateWaveformSegment,
        loadAudioFile,
        clearCache
    };
}
